import { h, Component, Prop, Element, Listen, Method, Host, State } from '@stencil/core';
import { LightStreamerSubscription as LS } from '../../utils/LightStreamerSubscription.js';
import { Page } from '../../utils/Page.js';

declare global {
  interface Window {
    PS: any;
  }
}
const PS = window.PS;

@Component({
  shadow: true,
  styleUrls: {
    default: 'igws-live-prices-carousel.default.less',
    rtl: 'igws-live-prices-carousel.rtl.less',
    rebrand: 'igws-live-prices-carousel.default.less',
    rtl_rebrand: 'igws-live-prices-carousel.rtl.less',
    prime: 'igws-live-prices-carousel.default.less',
    rtl_prime: 'igws-live-prices-carousel.rtl.less',
    investments: 'igws-live-prices-carousel.default.less',
    rtl_investments: 'igws-live-prices-carousel.rtl.less',
    helpandsupport: 'igws-live-prices-carousel.default.less',
    rtl_helpandsupport: 'igws-live-prices-carousel.rtl.less',
    academy: 'igws-live-prices-carousel.default.less',
    rtl_academy: 'igws-live-prices-carousel.rtl.less',
    landingpages: 'igws-live-prices-carousel.default.less',
    rtl_landingpages: 'igws-live-prices-carousel.rtl.less',
    generic: 'igws-live-prices-carousel.default.less',
    rtl_generic: 'igws-live-prices-carousel.rtl.less',
  },
  tag: 'igws-live-prices-carousel',
})
export class LivePricesCarouselComponent {
  private windowWidth = 0;
  private timeoutID: any;
  private epicsValues!: string[][];
  private nameUrlValues!: string[][];
  private fidsValues!: string[][];
  private scalingFactor = {};
  private price!: HTMLElement;

  @Element() el!: HTMLIgwsLivePricesCarouselElement;

  /**
   * Component epics to connect
   */
  @Prop() epics!: string;

  /**
   * Markets names and urls
   */
  @Prop() nameUrl!: string;

  /**
   * Component fids
   */
  @Prop() fids!: string;

  /**
   * Remove links from component
   */
  @Prop() removeLinks!: boolean;

  /**
   * Remove links for mobile breakpoint
   */
  @Prop() removeMobileLinks!: boolean;

  /**
   * CMS edit mode
   */
  @Prop() editMode!: boolean;

  @State() validFlags: Record<string, boolean> = {};

  @Listen('resize', { target: 'window' })
  handleResize(ev: UIEvent) {
    window.clearTimeout(this.timeoutID);
    this.timeoutID = window.setTimeout(() => {
      const target = ev.target as Window;
      const currentWidth =
        target.innerWidth || target.document.documentElement.clientWidth || target.document.body.clientWidth;
      if (currentWidth !== this.windowWidth) {
        this.windowWidth = currentWidth;
        this.resize();
      }
    }, 200);
    this.startScrollAnimation();
  }

  componentWillLoad() {
    this.windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.epicsValues = this.convertValues(this.epics);
    this.nameUrlValues = this.convertValues(this.nameUrl);
    this.fidsValues = this.convertValues(this.fids);

    this.nameUrlValues.forEach(([marketName]) => {
      const flagSrc = this.constructFlagPaths(marketName);
      const img = new Image();
      img.src = flagSrc;
      img.onload = () => {
        this.validFlags = { ...this.validFlags, [marketName]: true };
      };
      img.onerror = () => {
        this.validFlags = { ...this.validFlags, [marketName]: false };
      };
    });
  }

  componentDidLoad() {
    this.price = this.el.shadowRoot?.querySelector('.dynamic-table') as HTMLElement;
    const containers = this.el.shadowRoot?.querySelectorAll('.dynamic-table');
    containers?.forEach((container, idx) => {
      const containerWidth = (container as HTMLElement).scrollWidth;
      const animationDuration = `${containerWidth / 100}s`;
      (container as HTMLElement).style.setProperty(`--animation-duration-${idx}`, animationDuration);

      if (!this.editMode) {
        this.update = this.update.bind(this);
        LS.loadScript(this.setup.bind(this));
      }
    });
    this.startScrollAnimation();
  }

  private convertValues(str: string): string[][] {
    return str
      .replace(/\{|\}|/gi, '')
      .split(',')
      .map(f => f.split('=').map(s => s.trim()));
  }

  private getEpicID(index: any) {
    const epicsArray = this.epics.slice(1, -1).split(', ');
    return epicsArray[index].split('=')[0];
  }

  private setup() {
    const tables = this.el.shadowRoot?.querySelectorAll('.dynamic-table');
    tables?.forEach(table => {
      this.epicsValues.forEach((epic, index) => {
        const modifier = epic[1] ? `:${epic[1].replace(/_/gi, ',')}` : '';
        LS.subscribe(epic[0], modifier, table as HTMLElement, (price, fieldName, _fieldPos, newValue) => {
          this.update(price, fieldName, _fieldPos, newValue, index);
        });
      });
    });
    this.resize();
  }

  private update(price: HTMLElement, fieldName: string, _fieldPos: string, newValue: string, index: number) {
    const field = price.querySelectorAll(`[data-field$=${fieldName}]`)[index] as HTMLElement;
    const nValue = Number(newValue);

    if (fieldName === 'SCALING_FACTOR') {
      this.scalingFactor[index] = newValue || 1;
    }

    if (field != null) {
      field.classList.remove('price--up', 'price--down');

      switch (fieldName) {
        case 'BID':
        case 'OFR':
          const oldValue = Number(field.innerText);
          if (oldValue > nValue) {
            field.classList.add('price--down');
          } else if (oldValue < nValue) {
            field.classList.add('price--up');
          }
          break;

        case 'CPC':
          if (nValue < 0) {
            field.classList.add('price--down-cpc');
          } else if (nValue > 0) {
            field.classList.add('price--up-cpc');
          }
          break;
      }

      field.innerText = newValue;

      const bidField = price.querySelectorAll('[data-field$=BID]')[index] as HTMLElement;
      const ofrField = price.querySelectorAll('[data-field$=OFR]')[index] as HTMLElement;
      const midField = price.querySelectorAll('[data-field$=MID]')[index] as HTMLElement;

      if (fieldName === 'BID' || fieldName === 'OFR') {
        const bidValue = fieldName === 'BID' ? Number(newValue) : Number(bidField?.innerText || 0);
        const ofrValue = fieldName === 'OFR' ? Number(newValue) : Number(ofrField?.innerText || 0);
        const newMidValue = this.calculateMidPrice(bidValue, ofrValue);

        if (midField) {
          const oldMidValue = Number(midField.innerText);
          let midClass = '';

          if (oldMidValue > Number(newMidValue)) {
            midClass = 'price--down';
          } else if (oldMidValue < Number(newMidValue)) {
            midClass = 'price--up';
          }

          if (midClass) {
            midField.classList.add(midClass);

            if ((midField as any)._midTimeout) {
              clearTimeout((midField as any)._midTimeout);
            }

            (midField as any)._midTimeout = setTimeout(() => {
              midField.classList.remove('price--up', 'price--down');
            }, 500);
          }

          midField.innerText = newMidValue;
          midField.innerText = newMidValue;
          midField.innerText = newMidValue;
        }
      }
    }

    if (fieldName === 'BID' || fieldName === 'OFR') {
      this.setSpread(price, index);
    }
  }

  private calculateMidPrice(bid: number, ofr: number): string {
    const mid = (bid + ofr) / 2;
    return mid.toFixed(2);
  }

  /**
   * Resize the component and adjust the view for different screen sizes.
   * This method can be called externally to trigger the resizing logic,
   * which includes showing the columns and setting the mobile view.
    @returns {Promise<void>}
  */

  @Method()
  async resize(): Promise<void> {
    this.showColumns();
    this.setMobileView();
  }

  private isElementWiderThanParent(child: HTMLElement) {
    const html = document.documentElement;
    const childRect = child.getBoundingClientRect();
    const parentRect = this.el.getBoundingClientRect();
    return (
      childRect.left < parentRect.left ||
      childRect.right > parentRect.right ||
      (childRect.left < 0 || childRect.right) > (html.clientWidth || window.innerWidth)
    );
  }

  private setMobileView() {
    if (this.isElementWiderThanParent(this.price)) this.price.classList.add('dynamic-table--mobile');
  }

  private showColumns() {
    this.price.classList.remove('dynamic-table--mobile');
  }

  private handleRowClick(event: UIEvent) {
    const element = event && (event.target as IGHTMLElement);
    const isMobile = window.matchMedia('only screen and (max-width: 800px)').matches;
    if (this.removeMobileLinks && isMobile) {
      event.preventDefault();
    } else {
      const row = element.closest('.dynamic-table__row, .dynamic-table__iconrow') as HTMLElement;
      const link = row.querySelector('.market-epic-link') as HTMLAnchorElement;
      const trackingData = {
        name: link.textContent,
        epicID: link.getAttribute('data-epic'),
      };
      if (link && link.tagName === 'A') {
        Page.setLocationHref(link.getAttribute('href'));
        PS.GAtracking.marketDataInteractionEvent.prototype.setEvent(trackingData);
      }
    }
  }

  private setSpread(price: HTMLElement, index: number) {
    const ofrField = price.querySelectorAll('[data-field$=OFR]')[index] as HTMLElement;
    const bidField = price.querySelectorAll('[data-field$=BID]')[index] as HTMLElement;
    const sprdField = price.querySelectorAll('[data-field$=SPRD]')[index] as HTMLElement;
    const scalingFactor = this.scalingFactor[index] || 1;
    if (sprdField != null) {
      const ofrValue = Number(ofrField.innerText);
      const bidValue = Number(bidField.innerText);
      const sprdValue = (ofrValue - bidValue) * scalingFactor;

      if (typeof sprdValue === 'number' && sprdValue === sprdValue) {
        // validate that is not NaN
        sprdField.innerText = `${+parseFloat(`${sprdValue}`).toFixed(2)}`;
      }
    }
  }

  private startScrollAnimation() {
    const dynamicTable = this.el.shadowRoot?.querySelector('.main-container') as HTMLElement;
    if (dynamicTable) {
      const contentWidth = dynamicTable.scrollWidth;
      const containerWidth = this.el.getBoundingClientRect().width;
      const scrollingSpeed = 200;
      const duration = (contentWidth + containerWidth) / scrollingSpeed;

      const translateXDistance = -contentWidth + 'px';

      dynamicTable.style.setProperty('--scroll-duration', `${duration}s`);
      dynamicTable.style.setProperty('--scroll-translate', translateXDistance);
      dynamicTable.classList.add('scrolling');
    }
  }

  private constructFlagPaths(marketName: string): string {
    const basePath = '/content/dam/igcom-websites/common/icons/ig-retail/flags/';
    const sanitizedMarket = marketName.replace(/[^a-zA-Z0-9 ()-.]/g, '_');
    return `${basePath}${sanitizedMarket}.png`;
  }

  render() {
    return (
      <Host>
        <div class="main-container">
          {[...Array(2)].map((_, duplicationIndex) => (
            <section class="dynamic-table" key={duplicationIndex}>
              {this.nameUrlValues.map((epic, index) => {
                return (
                  <div
                    class={`
                    ${this.validFlags[epic[0]] ? 'dynamic-table__iconrow' : 'dynamic-table__row'}
                    ${this.removeLinks ? 'un' : 'clickable'} 
                    ${this.removeMobileLinks ? 'clickable--desktop' : ''}
                  `}
                    onClick={this.handleRowClick}
                    key={`${duplicationIndex}-${index}`}
                  >
                    {this.validFlags[epic[0]] && (
                      <div class="dynamic-table__icon">
                        <img src={this.constructFlagPaths(epic[0])} alt={`${epic[0]} flag`} />
                      </div>
                    )}
                    <div class="dynamic-table__epicsData">
                      <div class="dynamic-table__epicName">
                        {this.removeLinks || epic[1] === 'null' ? (
                          <span class="market-epic-link" data-epic={this.getEpicID(index)}>
                            {epic[0]}
                          </span>
                        ) : this.removeMobileLinks ? (
                          <a
                            href={epic[1]}
                            data-mobile={epic[0]}
                            class="market-epic-link"
                            data-epic={this.getEpicID(index)}
                          >
                            {epic[0]}
                          </a>
                        ) : (
                          <a href={epic[1]} class="market-epic-link" data-epic={this.getEpicID(index)}>
                            {epic[0]}
                          </a>
                        )}
                      </div>
                      <div class="dynamic-table__fids">
                        {this.fidsValues.map(fid => (
                          <div class="dynamic-table__cell" data-title={fid[1]} key={fid[0]}>
                            <span data-field={fid[0]}>-</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
        <slot name="disclaimer" />
      </Host>
    );
  }
}
